import { A, O, pipe } from "@mobily/ts-belt";
import type { Row, RowData, Table } from "@tanstack/react-table";

export function selectRange<T>(rows: Array<Row<T>>, index: number) {
  const previousSelectedRange = pipe(
    rows,
    A.slice(0, index + 1),
    A.reverse,
    (rows) =>
      pipe(
        rows,
        A.getIndexBy((row) => row.getIsSelected()),
        O.match(
          (idx) => rows.slice(0, idx),
          () => [],
        ),
      ),
  );
  const nextSelectedRange = pipe(rows, A.sliceToEnd(index), (rows) =>
    pipe(
      rows,
      A.getIndexBy((row) => row.getIsSelected()),
      O.match(
        (idx) => rows.slice(0, idx),
        () => [],
      ),
    ),
  );

  let selectedRows = rows.slice(index, index + 1);

  if (previousSelectedRange.length === 0 && nextSelectedRange.length > 0) {
    selectedRows = nextSelectedRange;
  } else if (
    nextSelectedRange.length === 0 &&
    previousSelectedRange.length > 0
  ) {
    selectedRows = previousSelectedRange;
  } else if (nextSelectedRange.length > 0 && previousSelectedRange.length > 0) {
    selectedRows =
      previousSelectedRange.length <= nextSelectedRange.length
        ? previousSelectedRange
        : nextSelectedRange;
  }

  return selectedRows;
}

export const handleRowSelection = <TData extends RowData>({
  table,
  row,
  selected,
}: {
  table: Table<TData>;
  row: Row<TData>;
  selected?: boolean;
}) => {
  table.setRowSelection((state) =>
    table.options.meta?.shiftHeld?.current === true
      ? {
          ...state,
          ...selectRange(table.getRowModel().rows, row.index).reduce(
            (acc, row) => {
              return {
                ...acc,
                [row.id]: selected == null ? !row.getIsSelected() : selected,
              };
            },
            {},
          ),
        }
      : {
          ...state,
          [row.id]: selected == null ? !row.getIsSelected() : selected,
        },
  );
};
